import PaymentMethodField from "../../components/Finance/PaymentMethodField"
import { useEffect, useState } from "react"
import { ArrowLeft, DollarSign, Search, WalletCards } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import toast from "react-hot-toast"
import { collectPayment, collectGeneralPayment } from "../../api/products/post"
import { isCatalogStore } from "../../utils/storeCategory"
import { searchDebtors, searchGeneralDebtors } from "../../api/products/get"
import { notifyDashboardRefresh } from "../../utils/dashboardRefresh"
import { getStoreExperience } from "../../utils/storeExperience"

export default function CollectPaymentPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const initialSale = (location.state as any)?.sale || null
  const [amount, setAmount] = useState("")
    const [paymentMethod, setPaymentMethod] = useState("CASH")
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [searchSale, setSearchSale] = useState<any>(initialSale)

  const { userdata } = useSelector((state: any) => state.UserReducer)
  const isGeneralStore = isCatalogStore(userdata?.store)
  const storeId = userdata?.store?._id
  const selectedSale = searchSale
  const amountPaid = selectedSale?.paid ?? selectedSale?.totalPaid ?? 0
  const remainingAmount = Number(selectedSale?.remaining || 0)
  const experience = getStoreExperience(userdata?.store)

  useEffect(() => {
    if (initialSale) {
      const initialName = initialSale?.clientName || initialSale?.name || "Client"
      const initialPhone = initialSale?.clientPhone || initialSale?.phone || ""
      setQuery(initialPhone ? `${initialName} • ${initialPhone}` : initialName)
    }
  }, [initialSale])

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([])
      return
    }

    const fetchDebtors = async () => {
      try {
        setLoading(true)
        const data = isGeneralStore
          ? await searchGeneralDebtors(storeId, query)
          : await searchDebtors(storeId, query)
        setResults(Array.isArray(data) ? data : data?.data || [])
      } catch (e) {
        console.error(e)
        setResults([])
      } finally {
        setLoading(false)
      }
    }

    const debounce = setTimeout(fetchDebtors, 300)
    return () => clearTimeout(debounce)
  }, [isGeneralStore, query, storeId])

  const handleConfirm = async () => {
    const value = Number(amount)

    if (!value || value <= 0) {
      setError("Montant invalide")
      return
    }

    if (!selectedSale) {
      setError("Selectionnez d'abord une vente a encaisser")
      return
    }

    if (value > Number(selectedSale.remaining || 0)) {
      setError("Le montant depasse la dette restante")
      return
    }

    try {
      setSubmitting(true)
      setError("")
      const payment = isGeneralStore
        ? await collectGeneralPayment(storeId, selectedSale._id, value, paymentMethod)
        : await collectPayment(selectedSale._id, storeId, value, paymentMethod)

      notifyDashboardRefresh()
      toast.success(`Paiement encaisse: ${value.toLocaleString()} FCFA`)
      navigate("/dashboard", { state: { payment } })
    } catch (e: any) {
      setError(e?.message || "Erreur lors de l'encaissement")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <section className={`relative overflow-hidden rounded-3xl border border-white/40 bg-gradient-to-br ${experience.stock.heroClass} p-6 text-white shadow-xl sm:p-7`}>
        <div className={`pointer-events-none absolute -top-10 right-0 h-36 w-36 rounded-full blur-3xl ${experience.stock.heroGlowClass}`} />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 transition hover:bg-white/20"
            >
              <ArrowLeft size={14} />
              Retour tableau de bord
            </button>
            <p className="mt-4 inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
              <WalletCards size={12} />
              Encaissement studio
            </p>
            <h1 className="mt-3 text-2xl font-bold sm:text-3xl">Encaisser un paiement</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/85">
              Recherchez un dossier client, verifiez le solde restant et enregistrez le versement sans passer par une modal.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <HeroStat label="Deja paye" value={`${Number(amountPaid || 0).toLocaleString()} FCFA`} />
            <HeroStat label="Reste du" value={`${remainingAmount.toLocaleString()} FCFA`} />
          </div>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <section className={`rounded-3xl border p-5 shadow-sm ${experience.stock.panelClass}`}>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setError("")
              }}
              placeholder={
                isGeneralStore
                  ? "Rechercher client, telephone, produit..."
                  : "Rechercher client, telephone..."
              }
              className="w-full rounded-2xl border border-slate-200 pl-9 px-3 py-3"
            />

            {results.length > 0 && (
              <div className="absolute z-20 mt-2 max-h-72 w-full overflow-y-auto divide-y overflow-hidden rounded-2xl border bg-white shadow-xl">
                {results.map((d) => (
                  <button
                    key={d._id}
                    onClick={() => {
                      setError("")
                      setQuery(`${d.clientName} • ${d.clientPhone}`)
                      setSearchSale(d)
                      setResults([])
                    }}
                    className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition hover:bg-slate-50"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-800">{d.clientName}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {d.modelId?.name || d.productName || d.label || "Produit"}
                      </p>
                      <p className="mt-1 text-xs text-slate-400">{d.clientPhone || "-"}</p>
                    </div>
                    <span className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                      {Number(d.remaining || 0).toLocaleString()} FCFA
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading && <p className="mt-2 text-xs text-slate-400">Recherche...</p>}
          {error && <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

          <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-semibold text-slate-800">Montant a encaisser</p>
            <div className="relative mt-3">
              <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Ex: 50000"
                className="w-full rounded-2xl border border-slate-200 pl-10 px-3 py-3 focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <PaymentMethodField value={paymentMethod} onChange={setPaymentMethod} disabled={submitting} />
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 transition hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting}
                className="rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Encaissement..." : "Encaisser"}
              </button>
            </div>
          </div>
        </section>

        <aside className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-emerald-50 p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-800">
              <WalletCards size={18} />
              <p className="font-semibold">Resume du dossier</p>
            </div>

            {selectedSale ? (
              <div className="mt-4 space-y-3 text-sm">
                <SummaryRow label="Produit" value={selectedSale.modelId?.name || selectedSale.productName || selectedSale.label || selectedSale.lastProduct || "-"} />
                <SummaryRow label="Client" value={selectedSale.clientName || "-"} />
                <SummaryRow label="Telephone" value={selectedSale.clientPhone || "-"} />
                <SummaryRow label="Deja paye" value={`${Number(amountPaid || 0).toLocaleString()} FCFA`} accent="success" />
                <SummaryRow label="Montant saisi" value={`${Number(amount || 0).toLocaleString()} FCFA`} />
                <SummaryRow label="Reste a payer" value={`${remainingAmount.toLocaleString()} FCFA`} accent="danger" strong />
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate-500">
                Aucun dossier selectionne. Commencez par rechercher un client ou une vente en attente.
              </p>
            )}
          </section>

          <section className="rounded-3xl border border-slate-200 bg-slate-950 p-5 text-white shadow-sm">
            <p className="font-semibold">Conseil rapide</p>
            <p className="mt-2 text-sm text-white/70">
              Selectionnez le bon dossier, verifiez le reste a payer, puis encaissez uniquement le montant recu pour garder un suivi propre.
            </p>
          </section>
        </aside>
      </div>
    </div>
  )
}

function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3 backdrop-blur-sm">
      <p className="text-xs uppercase tracking-[0.12em] text-white/75">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}

function SummaryRow({
  label,
  value,
  strong,
  accent,
}: {
  label: string
  value: string
  strong?: boolean
  accent?: "danger" | "success"
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span
        className={`text-right ${
          accent === "danger"
            ? "font-semibold text-rose-600"
            : accent === "success"
              ? "font-semibold text-emerald-600"
              : strong
                ? "font-semibold text-slate-900"
                : "text-slate-700"
        }`}
      >
        {value}
      </span>
    </div>
  )
}
